import "./conveyancingSteps.css";

import React from "react";
import { IconButton } from "@mui/material";

import { STEPS } from "../../data/content";

const SWIPE_DISTANCE = 50;

//The thin arrow from the original site (points right; flipped for "previous")
const Arrow = ({ flip = false }) => (
  <svg
    className={`steps-arrow-icon ${flip ? "is-flipped" : ""}`}
    viewBox={"0 0 200 200"}
    xmlns={"http://www.w3.org/2000/svg"}
    aria-hidden={"true"}
  >
    <path
      fill={"currentColor"}
      d={"M104.133 18.918l-9.431 8.947 61.913 65.269-137.498.938.089 13 138.096-.942-61.338 66.11 9.529 8.842 75.39-81.254-76.75-80.91z"}
    />
  </svg>
);

//Slideshow of the steps in a property transfer. Supports arrows, the step dots, keyboard arrows and swiping.
const ConveyancingSteps = () => {
  const [index, setIndex] = React.useState(0);
  const touchStartX = React.useRef(null);

  const goTo = (i) => setIndex((i + STEPS.length) % STEPS.length);
  const prev = () => goTo(index - 1);
  const next = () => goTo(index + 1);

  const onKeyDown = (e) => {
    if (e.key === "ArrowLeft") {
      prev();
    }
    else if (e.key === "ArrowRight") {
      next();
    }
  };

  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const onTouchEnd = (e) => {
    if (touchStartX.current === null) {
      return;
    }
    const distance = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (distance > SWIPE_DISTANCE) {
      prev();
    }
    else if (distance < -SWIPE_DISTANCE) {
      next();
    }
  };

  return (
    <div
      className={"steps"}
      role={"region"}
      aria-roledescription={"carousel"}
      aria-label={"The conveyancing process"}
      tabIndex={0}
      onKeyDown={onKeyDown}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <div className={"container steps-inner"}>
        <IconButton
          className={"steps-arrow"}
          aria-label={"Previous step"}
          onClick={prev}
        >
          <Arrow flip />
        </IconButton>

        {/* All slides share one grid cell so the height never jumps between steps */}
        <div
          className={"steps-slides"}
          aria-live={"polite"}
        >
          {STEPS.map((step, i) => (
            <div
              key={step.title}
              className={`step ${i === index ? "is-active" : ""}`}
              role={"group"}
              aria-roledescription={"slide"}
              aria-label={`Step ${i + 1} of ${STEPS.length}`}
              aria-hidden={i !== index}
            >
              <h3 className={"step-title"}>
                Step {i + 1}. {step.title}
              </h3>
              <ul className={"step-points"}>
                {step.points.map((point) => (
                  <li key={point}>{point}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <IconButton
          className={"steps-arrow"}
          aria-label={"Next step"}
          onClick={next}
        >
          <Arrow />
        </IconButton>
      </div>

      <div className={"steps-dots"}>
        {STEPS.map((step, i) => (
          <button
            key={step.title}
            type={"button"}
            className={`steps-dot ${i === index ? "is-active" : ""}`}
            aria-label={`Go to step ${i + 1}: ${step.title}`}
            aria-current={i === index ? "step" : undefined}
            onClick={() => goTo(i)}
          />
        ))}
      </div>
    </div>
  );
};

export default ConveyancingSteps;
